/*
================================================================================
File: src/pages/SellerLeads/EditLeadPage.js
Description: A detailed view for an existing seller lead, including all data points and actions.
================================================================================
Issues: Loan ID on AI query only pulls first letter.
Skip trace errors if no first owner and does not pull second owner.
*/
import React, { useState, useCallback } from "react";

import PageContent from "../../components/common/PageContent";
import Modal from "../../components/common/Modal";
import {
  getAuctionOptionLabelClasses,
  getBuyHoldLabelClasses,
  getContractDeedLabelClasses,
  getEquityPartnerLabelClasses,
  getFixFlipLabelClasses,
  getFlipOnWrapLabelClasses,
  getHouseSwapLabelClasses,
  getLeaseOptionLabelClasses,
  getListingLabelClasses,
  getMortAssignLabelClasses,
  getShortSaleLabelClasses,
  getWholeSaleLabelClasses,
} from "../../constants/stopLightClasses";
import { offerDescriptions } from "../../constants/options";
import { FormGroupSingle } from "../../components/common/FormGroup";

const DescriptionModal = ({ isOpen, onClose, title, description }) => {
  if (!isOpen) return null;
  return (
    <Modal show={isOpen} onClose={onClose} title={title} icon={
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H7a1 1 0 01-1-1v-6z" clipRule="evenodd" />
        </svg>
      }
      iconColor="blue"
    >
      <div>
        {description.split("\n").map((line, index) => (
          <p
            key={index}
            className="text-gray-600 mb-2 last:mb-0 dark:text-gray-200"
          >
            {line}
          </p>
        ))}
      </div>
    </Modal>
  );
};

const StoplightLabel = ({ label, value, className, onClick }) => (
  <button
    onClick={() => onClick(label)}
    className={`text-center p-2 m-1 rounded-md transition-transform transform hover:scale-105 ${className}`}
  >
    <div className="font-semibold text-sm uppercase">{label}</div>
    <div className="text-md">
      {typeof value === "number"
        ? `$${value.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`
        : value}
    </div>
  </button>
);

const QuickScorebaord = () => {
  const [error] = useState(null);
  const [modalContent, setModalContent] = useState({
    title: "",
    description: "",
  });
  const [lead, setLead] = useState({
    // Initialize all fields to prevent uncontrolled component warnings
    total_debt: 0,
    property_valued_price: 0,
    property_repair_costs: 0,
  });

  const [isDescriptionModalOpen, setIsDescriptionModalOpen] = useState(false);
  const ARV = parseFloat(lead.property_valued_price) || 0;
  const repairs = parseFloat(lead.property_repair_costs) || 0;
  const debt = parseFloat(lead.total_debt) || 0;

  const asisValue = ARV - repairs;
  const equity = asisValue - debt;
  const EQP = ARV > 0 ? equity / ARV : 0;
  const listingIsNotGreen = false; // Mocking this value from PHP
  const listed = "0";
  const state = "TX";
  const needHome = "0";
  const Lo1C = false;
  const needsale = "30";
  const loyCal = Math.max(debt, 0.9 * ARV);
  const valueNetProceedsRetail = equity;
  const isCommercial = false;

  const handleLabelClick = (title) => {
    setModalContent({
      title,
      description: offerDescriptions[title] || "No description available.",
    });
    setIsDescriptionModalOpen(true);
  };

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setLead((prevLead) => ({ ...prevLead, [name]: value }));
  }, []);

  return (
    <PageContent title="Seller Leads" subtitle="Quick Scoreboard" error={error}>
      <div className="p-8 space-y-8">
        {/* Hero Banner */}
        <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center gap-4 mb-2">
            <div className="p-3 bg-white/20 rounded-lg">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
              </svg>
            </div>
            <div>
              <h2 className="text-2xl font-bold">Investment Analysis</h2>
              <p className="text-emerald-100">Calculate deal profitability across multiple strategies</p>
            </div>
          </div>
        </div>

        {/* Input Section */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
            <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Property Valuation</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Property Value ($)
              </label>
              <FormGroupSingle
                type="number"
                name="property_valued_price"
                value={lead.property_valued_price}
                onChange={handleChange}
                placeholder="0"
              />
            </div>

            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Repair Costs ($)
              </label>
              <FormGroupSingle
                type="number"
                name="property_repair_costs"
                value={lead.property_repair_costs}
                onChange={handleChange}
                placeholder="0"
              />
            </div>

            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Total Debt ($)
              </label>
              <FormGroupSingle
                type="number"
                value={lead.total_debt}
                name="total_debt"
                onChange={handleChange}
                placeholder="0"
              />
            </div>
          </div>
        </div>

        {/* Scoring Cards */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
            <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                <path fillRule="evenodd" d="M4 5a2 2 0 012-2 1 1 0 000-2 4 4 0 00-4 4v10a4 4 0 004 4h12a4 4 0 004-4V5a4 4 0 00-4-4 1 1 0 000 2 2 2 0 012 2v10a2 2 0 01-2 2H6a2 2 0 01-2-2V5z" clipRule="evenodd" />
              </svg>
            </div>
            <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Deal Scoring</h3>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <StoplightLabel
            label="Wholesale"
            onClick={handleLabelClick}
            value={0.65 * ARV - repairs}
            className={getWholeSaleLabelClasses(EQP, listingIsNotGreen)}
          />
          <StoplightLabel
            label="Equity Partner"
            onClick={handleLabelClick}
            value={Math.max(debt, 0.5 * ARV - repairs)}
            className={getEquityPartnerLabelClasses(EQP, listingIsNotGreen)}
          />
          <StoplightLabel
            label="Short Sale"
            onClick={handleLabelClick}
            value={0.7 * ARV - repairs}
            className={getShortSaleLabelClasses(EQP, Lo1C, listingIsNotGreen)}
          />
          <StoplightLabel
            label="Fix & Flip"
            onClick={handleLabelClick}
            value={0.7 * ARV - repairs}
            className={getFixFlipLabelClasses(EQP, listingIsNotGreen)}
          />
          <StoplightLabel
            label="Flip on Wrap"
            onClick={handleLabelClick}
            value={0.75 * ARV - repairs}
            className={getFlipOnWrapLabelClasses(EQP, listingIsNotGreen)}
          />
          <StoplightLabel
            label="Mortgage Assn."
            onClick={handleLabelClick}
            value={debt}
            className={getMortAssignLabelClasses(
              EQP,
              Lo1C,
              listingIsNotGreen,
              ARV,
              debt,
              repairs
            )}
          />
          <StoplightLabel
            label="Buy & Hold"
            onClick={handleLabelClick}
            value={0.85 * ARV - repairs}
            className={getBuyHoldLabelClasses(EQP, listingIsNotGreen)}
          />
          <StoplightLabel
            label="MLS"
            onClick={handleLabelClick}
            value={asisValue}
            className={getListingLabelClasses(
              needsale,
              listed,
              valueNetProceedsRetail
            )}
          />
          <StoplightLabel
            label="Lease Option"
            onClick={handleLabelClick}
            value={loyCal}
            className={getLeaseOptionLabelClasses(
              state,
              EQP,
              Lo1C,
              listingIsNotGreen
            )}
          />
          <StoplightLabel
            label="Auction"
            value={0.85 * ARV - repairs}
            onClick={handleLabelClick}
            className={getAuctionOptionLabelClasses(EQP, listingIsNotGreen)}
          />
          <StoplightLabel
            label="House Swap"
            value={needHome === "1" ? "House" : "0"}
            className={getHouseSwapLabelClasses(
              EQP,
              needHome,
              listingIsNotGreen
            )}
          />
          <StoplightLabel
            label="Contract Deed"
            onClick={handleLabelClick}
            value={isCommercial ? (0.8 * ARV).toFixed(2) : "0"}
            className={getContractDeedLabelClasses(
              isCommercial,
              EQP,
              listingIsNotGreen
            )}
          />
          </div>
        </div>
      </div>

      <DescriptionModal
        isOpen={isDescriptionModalOpen}
        onClose={() => setIsDescriptionModalOpen(false)}
        title={modalContent.title}
        description={modalContent.description}
      />
    </PageContent>
  );
};

export default QuickScorebaord;
